package com.nonopn.skydome.common.app.pages.dashboard.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.pages.dashboard.Colors
import com.nonopn.skydome.common.app.pages.dashboard.formatThousands

@Composable
fun ButterflyReport(
    modifier: Modifier,
    data: Map<String, DashboardImpactReportV2.NegPosCount>
) {
    Top10DaysButterflyChart(
        modifier = modifier,
        entries = data.entries.toList()
    )
}


@Composable
private fun Top10DaysButterflyChart(
    modifier: Modifier = Modifier,
    entries: List<Map.Entry<String, DashboardImpactReportV2.NegPosCount>>,
) {
    val maxVal =
        entries.maxOfOrNull { maxOf(it.value.negative, it.value.positive) }?.coerceAtLeast(1) ?: 1
    val textMeasurer = rememberTextMeasurer()
    val rowHeightDp = 44.dp
    Canvas(modifier = modifier.fillMaxWidth().height(rowHeightDp * entries.size)) {
        val labelW = 64.dp.toPx()
        val halfW = (size.width - labelW) / 2f
        val barAreaW = halfW.coerceAtLeast(1f)
        val centerX = labelW + halfW
        val barH = 20.dp.toPx()
        val rowH = rowHeightDp.toPx()
        val dateStyle = TextStyle(
            color = Colors.markerLabel,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold
        )
        val totalStyle = TextStyle(color = Colors.axisLabel, fontSize = 10.sp)
        val whiteStyle = TextStyle(
            color = Color.White,
            fontSize = 10.sp,
            fontWeight = FontWeight.SemiBold
        )
        // Outside styles: used when bar is too narrow to contain the number inside
        val negOutsideStyle = TextStyle(
            color = DashboardColors.negativeColor,
            fontSize = 10.sp,
            fontWeight = FontWeight.SemiBold
        )
        val posOutsideStyle = TextStyle(
            color = DashboardColors.positiveColor,
            fontSize = 10.sp,
            fontWeight = FontWeight.SemiBold
        )
        // First pass: draw alternating row backgrounds
        entries.forEachIndexed { idx, _ ->
            val bgColor = if (idx % 2 != 0) Colors.rowAlt else Color.White
            drawRect(
                color = bgColor,
                topLeft = Offset(0f, idx * rowH),
                size = Size(size.width, rowH)
            )
        }
        // Draw center axis line on top of backgrounds
        drawLine(
            color = Colors.axisLine,
            start = Offset(centerX, 0f),
            end = Offset(centerX, size.height),
            strokeWidth = 1.dp.toPx()
        )
        // Second pass: draw bars and text per row
        entries.forEachIndexed { idx, entry ->
            val key = entry.key
            val cnt = entry.value
            val rowTop = idx * rowH
            val rowMidY = rowTop + rowH / 2f
            val barTop = rowMidY - barH / 2f
            // Date label: "DD/MM" extracted from "DD/MM/YYYY"
            val keyParts = key.split("/")
            val shortDate = keyParts[0] + "/" + (keyParts.getOrNull(1) ?: "")
            val total = cnt.negative + cnt.positive
            val dateTxt = textMeasurer.measure(shortDate, dateStyle)
            drawText(dateTxt, topLeft = Offset(4f, rowTop + 4.dp.toPx()))
            val totalTxt = textMeasurer.measure(total.formatThousands(), totalStyle)
            drawText(
                totalTxt,
                topLeft = Offset(4f, rowTop + rowH - totalTxt.size.height - 4.dp.toPx())
            )
            // CG− bar (grows leftward from center axis)
            val negFrac = cnt.negative.toFloat() / maxVal
            val negBarW = (negFrac * barAreaW).coerceAtLeast(0f)
            if (negBarW > 0f) {
                drawRect(
                    color = DashboardColors.negativeColor,
                    topLeft = Offset(centerX - negBarW, barTop),
                    size = Size(negBarW, barH)
                )
                val negTxt = textMeasurer.measure(cnt.negative.formatThousands(), whiteStyle)
                if (negBarW >= negTxt.size.width + 8.dp.toPx()) {
                    // Inside bar (white text)
                    drawText(
                        negTxt,
                        topLeft = Offset(
                            centerX - negBarW + 4.dp.toPx(),
                            barTop + (barH - negTxt.size.height) / 2f
                        )
                    )
                } else {
                    // Bar too narrow: draw number outside (left of bar tip), in bar color
                    val negTxtOut = textMeasurer.measure(cnt.negative.formatThousands(), negOutsideStyle)
                    drawText(
                        negTxtOut,
                        topLeft = Offset(
                            (centerX - negBarW - negTxtOut.size.width - 4.dp.toPx()).coerceAtLeast(labelW),
                            barTop + (barH - negTxtOut.size.height) / 2f
                        )
                    )
                }
            }
            // CG+ bar (grows rightward from center axis)
            val posFrac = cnt.positive.toFloat() / maxVal
            val posBarW = (posFrac * barAreaW).coerceAtLeast(0f)
            if (posBarW > 0f) {
                drawRect(
                    color = DashboardColors.positiveColor,
                    topLeft = Offset(centerX, barTop),
                    size = Size(posBarW, barH)
                )
                val posTxt = textMeasurer.measure(cnt.positive.formatThousands(), whiteStyle)
                if (posBarW >= posTxt.size.width + 8.dp.toPx()) {
                    // Inside bar (white text)
                    drawText(
                        posTxt,
                        topLeft = Offset(
                            centerX + posBarW - posTxt.size.width - 4.dp.toPx(),
                            barTop + (barH - posTxt.size.height) / 2f
                        )
                    )
                } else {
                    // Bar too narrow: draw number outside (right of bar tip), in bar color
                    val posTxtOut = textMeasurer.measure(cnt.positive.formatThousands(), posOutsideStyle)
                    drawText(
                        posTxtOut,
                        topLeft = Offset(
                            centerX + posBarW + 4.dp.toPx(),
                            barTop + (barH - posTxtOut.size.height) / 2f
                        )
                    )
                }
            }
        }
    }
}

@Preview
fun ButterflyReportPreview() {

}
